import { z } from 'zod';
/**
 * The daily check-in.
 *
 * A child says how they are feeling and may write anything they like alongside
 * it. Both go to their grown-ups unedited and uninterpreted.
 *
 * Two rules hold this together:
 *
 *  1. **It is asked once a day, before the adventure, and answered.** The
 *     parent's call. The cost to watch for is a child tapping the nearest face
 *     to get through to their game, which is why no option is preselected and
 *     the faces are large and deliberate.
 *  2. **No reward is ever attached to the mood.** Rewarding cheerful answers
 *     teaches a child to lie to the people who most need the truth. This one is
 *     not negotiable: attaching anything earnable to a feeling would make the
 *     whole feature worse than useless.
 *
 * The screen does NOT tell the child their grown-ups read this. That is the
 * parent's decision, made so the answers stay unguarded. Worth knowing what it
 * costs: the moment a parent acts on what they read, the child works out that
 * it was read. The recommendation on record is that the parent tells them once,
 * in person - which keeps the screen uncluttered AND keeps the child informed.
 *
 * Nothing here is a measurement of a child's wellbeing, and no screen may
 * present it as one. It is what a child tapped on a particular afternoon.
 */
/** Ordered lowest to highest. The number is for sorting and never shown to a child. */
export const MOODS = [
    { key: 'super-sad', value: 1, childLabel: 'Super sad', parentLabel: 'super sad', face: 'rough' },
    { key: 'sad', value: 2, childLabel: 'A bit sad', parentLabel: 'a bit sad', face: 'low' },
    { key: 'ok', value: 3, childLabel: 'OK', parentLabel: 'OK', face: 'ok' },
    { key: 'happy', value: 4, childLabel: 'Happy', parentLabel: 'happy', face: 'good' },
    {
        key: 'super-happy',
        value: 5,
        childLabel: 'Super happy',
        parentLabel: 'super happy',
        face: 'great',
    },
];
export const MOOD_KEYS = MOODS.map((m) => m.key);
export const moodKeySchema = z.enum(MOOD_KEYS);
export function findMood(key) {
    return MOODS.find((m) => m.key === key) ?? null;
}
export function moodValue(key) {
    return findMood(key)?.value ?? 0;
}
/** The two lowest points on the scale. Used by the surfacing rules. */
export const LOW_MOODS = ['super-sad', 'sad'];
export function isLowMood(key) {
    return LOW_MOODS.includes(key);
}
/**
 * What a child sends. `note` is optional because plenty of days have nothing to
 * add, and demanding an explanation for a feeling is its own kind of pressure.
 */
export const checkInInput = z.object({
    mood: moodKeySchema,
    note: z.string().trim().max(2000).optional(),
});
/**
 * Real-world quests: a short thing to do away from the screen.
 *
 * The child says whether they did it, and that is accepted. Nothing verifies a
 * quest and nothing is taken away for not doing one - an app that treated a
 * six-year-old's word as evidence to be checked would poison exactly the
 * habit it is trying to build.
 */
export const questDoneInput = z.object({
    note: z.string().trim().max(1000).optional(),
});
export const QUEST_STATUSES = ['set', 'done'];
//# sourceMappingURL=checkin.js.map